package com.partejar.app.feature.gestantes.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.gestantes.data.GestanteClinicalDataSource
import com.partejar.app.feature.gestantes.data.GestanteClinicalDetail
import com.partejar.app.feature.gestantes.data.GestanteClinicalUpdate
import com.partejar.app.feature.gestantes.data.GestanteExam
import com.partejar.app.feature.gestantes.data.GestanteExamInput
import com.partejar.app.feature.gestantes.data.GestanteExamResultInput
import com.partejar.app.feature.gestantes.data.GestanteDeliveryInput
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime

sealed interface GestanteDetailUiState {
    data object Loading : GestanteDetailUiState
    data class Content(
        val detail: GestanteClinicalDetail,
        val editing: Boolean = false,
        val saving: Boolean = false,
        val actionError: String? = null,
        val actionSuccess: String? = null,
        val actionRevision: Long = 0
    ) : GestanteDetailUiState
    data class Error(val message: String) : GestanteDetailUiState
}

class GestanteDetailViewModel private constructor(
    private val source: GestanteClinicalDataSource,
    private val scopeOverride: CoroutineScope?,
    @Suppress("UNUSED_PARAMETER") marker: Unit
) : ViewModel() {
    constructor(source: GestanteClinicalDataSource) : this(source, null, Unit)
    internal constructor(source: GestanteClinicalDataSource, scope: CoroutineScope) : this(source, scope, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _state = MutableStateFlow<GestanteDetailUiState>(GestanteDetailUiState.Loading)
    val state = _state.asStateFlow()

    fun load(id: Long) = scope.launch {
        _state.value = when (val current = _state.value) {
            is GestanteDetailUiState.Content -> current.copy(saving = true, actionError = null)
            else -> GestanteDetailUiState.Loading
        }
        try {
            val detail = source.detail(id)
            _state.value = GestanteDetailUiState.Content(detail.copy(
                exams = source.exams(id),
                examTypes = source.examCatalog()
            ))
        } catch (error: CancellationException) {
            throw error
        } catch (_: Throwable) {
            _state.value = GestanteDetailUiState.Error("Não foi possível carregar a gestante.")
        }
    }

    fun setEditing(editing: Boolean) {
        val current = _state.value as? GestanteDetailUiState.Content ?: return
        _state.value = current.copy(editing = editing, actionError = null)
    }

    fun save(id: Long, update: GestanteClinicalUpdate) = action {
        if (update.dueDate != null && runCatching { LocalDate.parse(update.dueDate) }.isFailure) {
            throw IllegalArgumentException("Informe uma DPP válida no formato AAAA-MM-DD.")
        }
        source.update(id, update)
    }

    fun addComorbidity(id: Long, description: String, observation: String?) = action { current ->
        if (description.isBlank()) throw IllegalArgumentException("Informe a comorbidade.")
        val added = source.addComorbidity(id, description, observation)
        current.detail.copy(comorbidities = current.detail.comorbidities + added)
    }

    fun updateComorbidity(id: Long, comorbidityId: Long, observation: String?) = action { current ->
        val updated = source.updateComorbidity(id, comorbidityId, observation)
        current.detail.copy(comorbidities = current.detail.comorbidities.map {
            if (it.id == updated.id) updated else it
        })
    }

    fun deactivateComorbidity(id: Long, comorbidityId: Long) = action { current ->
        val updated = source.deactivateComorbidity(id, comorbidityId)
        current.detail.copy(comorbidities = current.detail.comorbidities.map { if (it.id == comorbidityId) updated else it })
    }

    fun createExam(id: Long, input: GestanteExamInput) = action(successMessage = "Exame salvo com sucesso.") { current ->
        if (input.performedAt.isBlank()) throw IllegalArgumentException("Informe a data do exame.")
        if (runCatching { LocalDateTime.parse(input.performedAt) }.isFailure) {
            throw IllegalArgumentException("Informe uma data válida (dd/MM/aaaa) e uma hora válida (HH:mm).")
        }
        input.results.forEach(::validarResultado)
        val created = source.createExam(id, input)
        current.detail.copy(exams = listOf(created) + current.detail.exams)
    }

    fun addExamResult(id: Long, examId: Long, input: GestanteExamResultInput) = action(successMessage = "Resultado adicionado com sucesso.") { current ->
        validarResultado(input)
        val updated = source.addExamResult(id, examId, input)
        current.detail.copy(exams = current.detail.exams.replaceExam(updated))
    }

    fun updateExamResult(
        id: Long,
        examId: Long,
        resultId: Long,
        input: GestanteExamResultInput
    ) = action(successMessage = "Resultado corrigido com sucesso.") { current ->
        validarResultado(input)
        val updated = source.updateExamResult(id, examId, resultId, input)
        current.detail.copy(exams = current.detail.exams.replaceExam(updated))
    }

    fun registerDelivery(id: Long, input: GestanteDeliveryInput) = action { current ->
        if (runCatching { LocalDate.parse(input.date) }.isFailure) throw IllegalArgumentException("Informe a data do parto no formato AAAA-MM-DD.")
        if (input.time != null && runCatching { kotlinx.datetime.LocalTime.parse(input.time) }.isFailure) throw IllegalArgumentException("Informe a hora no formato HH:MM.")
        if (input.location.isBlank()) throw IllegalArgumentException("Informe o local do parto.")
        source.registerDelivery(id, input)
    }

    private fun validarResultado(input: GestanteExamResultInput) {
        if ((input.analysisId == null && input.parameter.isBlank()) || input.value.isBlank()) {
            throw IllegalArgumentException("Informe o parâmetro e o valor do resultado.")
        }
        if ((input.freeResult == true && (input.analysisId != null || input.analysisUnitId != null)) ||
            (input.analysisId == null && (input.analysisUnitId != null || input.freeResult == false))) {
            throw IllegalArgumentException("Selecione uma análise ou Outro resultado.")
        }
    }

    fun clearActionFeedback() {
        val current = _state.value as? GestanteDetailUiState.Content ?: return
        _state.value = current.copy(actionError = null, actionSuccess = null)
    }

    private fun action(successMessage: String? = null, operation: suspend (GestanteDetailUiState.Content) -> GestanteClinicalDetail) = scope.launch {
        val current = _state.value as? GestanteDetailUiState.Content ?: return@launch
        if (current.saving) return@launch
        _state.value = current.copy(saving = true, actionError = null, actionSuccess = null)
        try {
            _state.value = GestanteDetailUiState.Content(operation(current), editing = false,
                actionSuccess = successMessage, actionRevision = current.actionRevision + 1)
        } catch (error: CancellationException) {
            throw error
        } catch (error: IllegalArgumentException) {
            _state.value = current.copy(saving = false, actionError = error.message, actionSuccess = null)
        } catch (_: Throwable) {
            _state.value = current.copy(saving = false, actionError = "Não foi possível salvar as alterações.", actionSuccess = null)
        }
    }

}

private fun List<GestanteExam>.replaceExam(updated: GestanteExam): List<GestanteExam> = map {
    if (it.id == updated.id) updated else it
}
